import React, { useState } from "react";
import api from "components/settings/api";

import PropTypes from "prop-types";
import ModalErrorHandler from "./ModalErrorHandler";
import { toast, Bounce } from "react-toastify";
import CustomToastMessage from "../common/CustomToastMessage";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, Grid, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  modalWrapper: {
    position: "fixed",
    placeContent: "center",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: "100%",
    height: "100%",
    overflow: "auto",
    zIndex: "10002",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: "#00000098",
    paddingRight: 17,
    "@media screen and (maxWidth: 700)": {
      placeContent: "flex-start",
    },
  },
  containerInner: {
    padding: "20px",
    width: "100%",
    "@media screen and (max-width: 500px)": {
      padding: "0 16px",
    },
    gap: "0.5rem",
  },
  paper: {
    borderRadius: "5px",
    boxShadow: "2.5px 5px #464646",
    width: "500px",
    "@media screen and (max-width: 450px)": {
      width: "90%",
    },
  },
  title: {
    fontFamily: "Poppins",
    fontWeight: 700,
    fontSize: "24px",
    marginTop: "1rem",
  },
  subtitle: {
    fontFamily: "Open Sans",
    fontWeight: "normal",
    fontSize: 14,
    margin: "0 1rem",
    lineHeight: "150%",
    color: "rgba(0, 27, 17, 0.5)",
  },
  sub_title: {
    width: "100%",
  },
  buttons: {
    color: "white",
    fontFamily: "open sans",
    minWidth: "10rem",
    backgroundColor: theme.palette.primary.main,
    margin: "-.5rem .5rem .75rem",
    width: "100%"
  },
}));

const OutOfCredits = ({ toggleModal }) => {
  const classes = useStyles();

  // Input field state
  const initialRequestData = {
    title: "Requesting another team",
    content: "",
    email: "",
  };

  // Form Input State
  const [{ title, content, email }, setRequestData] =
    useState(initialRequestData);

  // Errors State
  const [showErrors, setShowErrors] = useState(false);
  const [errorsArr, setErrorsArr] = useState();

  // State updates on input
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setRequestData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Toast message function
  const notify = () =>
    toast.info(
      <CustomToastMessage message="Thanks, we will look over your request." />,
      {
        autoClose: 2000,
        closeButton: true,
        hideProgressBar: false,
        transition: Bounce,
        position: "bottom-center",
      }
    );

  // Form submission
  const handleSubmit = async (event) => {
    try {
      if (event) {
        event.preventDefault();
      }

      const payload = {
        title,
        content,
        email,
      };

      // Send post request for signup
      const res = await api.post("/api/v1/feedback/team", payload);

      // If no validation errors were found
      if (res.data.validationErrors === undefined) {
        // Clear any errors
        setErrorsArr([]);
        // Hide the errors component
        setShowErrors(false);
        // Toggle toast message
        notify();
        // Toggle the modal
        toggleModal();
      } else {
        // Set errors
        setErrorsArr(res.data.validationErrors.errors);
        // Show the errors component
        setShowErrors(true);
      }
    } catch (err) {
      throw new Error(err);
    }
  };

  return (
    <div className={classes.modalWrapper}>
      <Paper variant="outlined" className={classes.paper}>
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Grid item xxs={9.9} xs={8.8}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <div className={classes.title}>Not enough tokens</div>
              </Grid>
              <Grid item>
                <Button
                  disableRipple={true}
                  variant="text"
                  className="button--modal_cancelJob"
                  aria-label="Close modal"
                  onClick={toggleModal}
                >
                  <CloseRoundedIcon fontSize="inherit" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className={classes.containerInner}
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <h3 className="header header--modal_sub_heading">
            Tell us why you want to create this team.
          </h3>
          <Grid item xs={12} sx={{ width: "100%" }}>
            <Grid container direction="column">
              <form
                className="form--modal_form"
                onSubmit={handleSubmit}
                method="POST"
                aria-label="Request another team"
              >
                {showErrors && <ModalErrorHandler errorsArr={errorsArr} />}
                <label className="label--form_label" htmlFor="title">
                  Subject
                  <input
                    className="input input--form_input"
                    type="text"
                    name="title"
                    value={title}
                    placeholder="Requesting another team"
                    minLength="4"
                    maxLength="50"
                    onChange={handleInputChange}
                    required
                    aria-label="Subject"
                  />
                </label>
                <label className="label--form_label" htmlFor="email">
                  Your Email
                  <input
                    className="input input--form_input"
                    type="email"
                    name="email"
                    maxLength="50"
                    value={email}
                    onChange={handleInputChange}
                    required
                    aria-label="Email"
                  />
                </label>
                <label className="label--form_label" htmlFor="content">
                  New Team Purpose
                  <textarea
                    className="input input--form_input"
                    type="text"
                    name="content"
                    row="2"
                    cols="1"
                    value={content}
                    onChange={handleInputChange}
                    minLength="1"
                    maxLength="600"
                    required
                    aria-label="Content"
                  />
                </label>
                <div className={classes.sub_title}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid
                      item
                      xs={12}
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Button
                        type="submit"
                        value="Request Tokens"
                        variant="contained"
                        color="primary"
                        className={classes.buttons}
                      >
                        Request Tokens
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </form>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default OutOfCredits;

OutOfCredits.propTypes = {
  toggleModal: PropTypes.func,
};
